/**
 * Conversation lifecycle wiring lifted out of ChatWidget: `conversation_end`
 * analytics (manual close, page hide, idle timeout), the idle follow-up prompt,
 * faster thread polling while escalated, and the escalation fallback monitor.
 *
 * `chat` and `thread` are the memoized hook handles; they stay in the callback
 * dependency lists exactly as the component had them.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { IdleTimeoutConfig } from '../types';
import type { WidgetStrings } from '../utils/i18n';
import type { useChat } from './useChat';
import type { useThread } from './useThread';
import { useEscalationMonitor } from './useEscalationMonitor';
import { useIdleTimeout } from './useIdleTimeout';

type ChatHandle = ReturnType<typeof useChat>;
type ThreadHandle = ReturnType<typeof useThread>;
type TrackAnalytics = (action: string, data: Record<string, unknown>, transport?: 'beacon') => void;

export interface UseConversationLifecycleOptions {
  chat: Pick<ChatHandle, 'state' | 'dispatch' | 'setMessages'>;
  thread: Pick<ThreadHandle, 'threadId' | 'setThreadId'>;
  minimized: boolean;
  trackAnalytics: TrackAnalytics;
  strings: Pick<WidgetStrings, 'handoffFallback' | 'idlePrompt' | 'sessionEnded'>;
  idleTimeoutConfig?: IdleTimeoutConfig;
  /** Configured thread poll interval (ms, 0 = off); escalation may speed it up. */
  threadPollIntervalMs: number;
  threadPollIntervalMsEffective: number;
  setThreadPollIntervalMsEffective: (ms: number) => void;
}

export function useConversationLifecycle({
  chat,
  thread,
  minimized,
  trackAnalytics,
  strings,
  idleTimeoutConfig,
  threadPollIntervalMs,
  threadPollIntervalMsEffective,
  setThreadPollIntervalMsEffective,
}: UseConversationLifecycleOptions): void {
  const previousMinimizedRef = useRef(minimized);
  useEffect(() => {
    const wasOpen = previousMinimizedRef.current === false;
    const isNowClosed = minimized === true;
    previousMinimizedRef.current = minimized;
    if (!wasOpen || !isNowClosed || chat.state.messages.length === 0) return;
    trackAnalytics('conversation_end', {
      reason: 'manual_close',
      threadId: thread.threadId || undefined,
      totalMessages: chat.state.messages.length,
    });
  }, [chat.state.messages.length, minimized, thread.threadId, trackAnalytics]);

  const conversationEndFiredRef = useRef(false);
  const conversationEndContextRef = useRef({
    messageCount: 0,
    threadId: null as string | null,
  });
  useEffect(() => {
    conversationEndContextRef.current = {
      messageCount: chat.state.messages.length,
      threadId: thread.threadId || null,
    };
  }, [chat.state.messages.length, thread.threadId]);
  useEffect(() => {
    const handlePageHide = () => {
      if (conversationEndFiredRef.current) return;
      const { messageCount, threadId } = conversationEndContextRef.current;
      if (messageCount === 0) return;
      conversationEndFiredRef.current = true;
      trackAnalytics(
        'conversation_end',
        {
          reason: 'page_hide',
          threadId: threadId || undefined,
          totalMessages: messageCount,
        },
        'beacon'
      );
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [trackAnalytics]);

  useEffect(() => {
    const nextIntervalMs =
      chat.state.isEscalated && (!threadPollIntervalMs || threadPollIntervalMs <= 0)
        ? 3000
        : threadPollIntervalMs;
    if (nextIntervalMs !== threadPollIntervalMsEffective) {
      setThreadPollIntervalMsEffective(nextIntervalMs);
    }
  }, [
    chat.state.isEscalated,
    threadPollIntervalMs,
    threadPollIntervalMsEffective,
    setThreadPollIntervalMsEffective,
  ]);

  useEscalationMonitor({
    isEscalated: chat.state.isEscalated,
    messages: chat.state.messages,
    streamingMessage: chat.state.streamingMessage,
    dispatch: chat.dispatch,
    trackAnalytics,
    fallbackMessage: strings.handoffFallback,
  });

  const idleTimeoutClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { resetActivity: resetIdleTimer } = useIdleTimeout({
    enabled: !!idleTimeoutConfig?.enabled && chat.state.messages.length > 0,
    idleMs: idleTimeoutConfig?.idleMs,
    closeMs: idleTimeoutConfig?.closeMs,
    hasMessages: chat.state.messages.length > 0,
    onIdle: useCallback(() => {
      const now = new Date();
      trackAnalytics('conversation_idle_prompt', {
        threadId: thread.threadId || undefined,
        messageCount: chat.state.messages.length,
        idleMs: idleTimeoutConfig?.idleMs,
      });
      chat.dispatch({
        type: 'ADD_MESSAGE',
        message: {
          id: `idle-${now.getTime()}`,
          sender: 'ai',
          text: strings.idlePrompt,
          timestamp: now.toISOString(),
          status: 'delivered',
        },
      });
    }, [chat, idleTimeoutConfig?.idleMs, thread.threadId, trackAnalytics, strings.idlePrompt]),
    onTimeout: useCallback(() => {
      const now = new Date();
      trackAnalytics('conversation_end', {
        reason: 'idle_timeout',
        threadId: thread.threadId || undefined,
        totalMessages: chat.state.messages.length,
        idleMs: idleTimeoutConfig?.idleMs,
        closeMs: idleTimeoutConfig?.closeMs,
      });
      chat.dispatch({
        type: 'ADD_MESSAGE',
        message: {
          id: `timeout-${now.getTime()}`,
          sender: 'ai',
          text: strings.sessionEnded,
          timestamp: now.toISOString(),
          status: 'delivered',
        },
      });
      // Clear session after a brief delay so the user sees the goodbye message
      if (idleTimeoutClearRef.current) {
        clearTimeout(idleTimeoutClearRef.current);
      }
      idleTimeoutClearRef.current = setTimeout(() => {
        chat.setMessages([]);
        if (thread.setThreadId) thread.setThreadId(null);
      }, 3000);
    }, [
      chat,
      idleTimeoutConfig?.closeMs,
      idleTimeoutConfig?.idleMs,
      thread,
      trackAnalytics,
      strings.sessionEnded,
    ]),
  });

  useEffect(() => {
    return () => {
      if (idleTimeoutClearRef.current) {
        clearTimeout(idleTimeoutClearRef.current);
      }
    };
  }, []);

  // Reset idle timer whenever messages change (user sent something)
  const prevMsgCountRef = useRef(chat.state.messages.length);
  useEffect(() => {
    if (chat.state.messages.length > prevMsgCountRef.current) {
      const lastMsg = chat.state.messages[chat.state.messages.length - 1];
      if (lastMsg?.sender === 'user') {
        resetIdleTimer();
      }
    }
    prevMsgCountRef.current = chat.state.messages.length;
  }, [chat.state.messages, resetIdleTimer]);
}
